import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { leadsApi } from "@/lib/api";

// Leads are saved to the API database directly via leadsApi.create()

const leadSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(100),
  phone: z
    .string()
    .min(10, "Please enter a valid phone number")
    .max(15)
    .regex(/^[0-9+\-\s]+$/, "Please enter a valid phone number"),
  email: z.string().email("Please enter a valid email").max(255),
  budget: z.string().optional(),
  propertyType: z.string().optional(),
  location: z.string().optional(),
});

type LeadFormData = z.infer<typeof leadSchema>;

interface LeadFormProps {
  source?: string;
  propertyId?: string;
  onSuccess?: () => void;
  compact?: boolean;
  className?: string;
}

const budgetOptions = [
  "Under ₹50 Lakhs",
  "₹50 Lakhs - ₹1 Cr",
  "₹1 Cr - ₹2 Cr",
  "₹2 Cr - ₹5 Cr",
  "₹5 Cr+",
];

const propertyTypes = [
  "Apartment",
  "Villa",
  "Penthouse",
  "Commercial",
  "Plot",
];

const locations = [
  "Gurugram",
  "Delhi NCR",
  "Noida",
  "Mumbai",
  "Bangalore",
  "Other",
];

function saveLeadFallback(data: LeadFormData, source: string) {
  try {
    const existing = JSON.parse(localStorage.getItem('admin_leads_v1') || '[]');
    const newLead = {
      id: Date.now(),
      name: data.name, email: data.email, phone: data.phone,
      message: `Budget: ${data.budget || 'N/A'}, Type: ${data.propertyType || 'N/A'}, Location: ${data.location || 'N/A'}`,
      property_interest: data.propertyType || '', budget_range: data.budget || '',
      source: source === 'contact-page' ? 'contact_form' : source === 'popup' ? 'popup' : 'property_inquiry',
      status: 'new' as const, notes: '',
      created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
    };
    existing.unshift(newLead);
    localStorage.setItem('admin_leads_v1', JSON.stringify(existing));
  } catch {}
}

export default function LeadForm({
  source = "website",
  propertyId,
  onSuccess,
  compact = false,
  className = "",
}: LeadFormProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
  });

  const onSubmit = async (data: LeadFormData) => {
    setIsSubmitting(true);
    
    // Try API first
    const apiSource = source === 'contact-page' ? 'contact_form' : source === 'popup' ? 'popup' : 'property_inquiry';
    const response = await leadsApi.create({
      name: data.name,
      email: data.email,
      phone: data.phone,
      message: `Budget: ${data.budget || 'N/A'}, Type: ${data.propertyType || 'N/A'}, Location: ${data.location || 'N/A'}`,
      property_interest: data.propertyType || '',
      budget_range: data.budget || '',
      source: apiSource as any,
      status: 'new',
    });

    // Fallback to localStorage only if API fails
    if (!response.success) {
      saveLeadFallback(data, source);
    }
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success("Thank you! Our team will contact you shortly.");
    
    if (onSuccess) {
      setTimeout(onSuccess, 2000);
    }
    
    reset();
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`text-center py-8 ${className}`}
      >
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h4 className="font-serif text-xl font-semibold mb-2">
          Thank You!
        </h4>
        <p className="text-muted-foreground text-sm">
          Our property expert will contact you within 24 hours.
        </p>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`space-y-4 ${className}`}
    >
      {/* Name & Phone - Always show */}
      <div className={compact ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        <div>
          <input
            {...register("name")}
            type="text"
            placeholder="Your Name *"
            className="w-full px-4 py-3 border border-border rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
          />
          {errors.name && (
            <p className="text-destructive text-xs mt-1">{errors.name.message}</p>
          )}
        </div>
        <div>
          <input
            {...register("phone")}
            type="tel"
            placeholder="Phone Number *"
            className="w-full px-4 py-3 border border-border rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
          />
          {errors.phone && (
            <p className="text-destructive text-xs mt-1">{errors.phone.message}</p>
          )}
        </div>
      </div>

      {/* Email */}
      <div>
        <input
          {...register("email")}
          type="email"
          placeholder="Email Address *"
          className="w-full px-4 py-3 border border-border rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
        />
        {errors.email && (
          <p className="text-destructive text-xs mt-1">{errors.email.message}</p>
        )}
      </div>

      {/* Optional Fields - Hide in compact mode */}
      {!compact && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              {...register("budget")}
              className="w-full px-4 py-3 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all appearance-none cursor-pointer"
            >
              <option value="">Select Budget</option>
              {budgetOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select
              {...register("propertyType")}
              className="w-full px-4 py-3 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all appearance-none cursor-pointer"
            >
              <option value="">Property Type</option>
              {propertyTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <select
            {...register("location")}
            className="w-full px-4 py-3 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all appearance-none cursor-pointer"
          >
            <option value="">Preferred Location</option>
            {locations.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-gold w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Submitting...
          </>
        ) : (
          "Get Best Deals"
        )}
      </button>

      <p className="text-muted-foreground text-xs text-center">
        By submitting, you agree to our{" "}
        <a href="/privacy" className="text-accent hover:underline">
          Privacy Policy
        </a>
      </p>
    </form>
  );
}
